// AdminProfile — admin self-service page: shows the current recovery e-mail
// status (masked) and allows setting/changing it. Always asks for the
// current password to authorise a channel change.
import React, { useEffect, useState } from 'react';
import { adminAxios, useAdminAuth } from './AdminAuthContext';
import {
  Mail, ShieldCheck, AlertTriangle, Loader2, CheckCircle2, KeyRound, RefreshCw,
} from 'lucide-react';

const AdminProfile = () => {
  const { token } = useAdminAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [novoEmail, setNovoEmail] = useState('');
  const [senhaAtual, setSenhaAtual] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type:'ok'|'err', msg}

  const reload = async () => {
    setLoading(true);
    try {
      const r = await adminAxios(token).get('/api/admin/me');
      setMe(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);
    if (!novoEmail.includes('@')) return setFeedback({ type: 'err', msg: 'Informe um e-mail válido.' });
    if (!senhaAtual) return setFeedback({ type: 'err', msg: 'Informe sua senha atual para confirmar.' });
    setSubmitting(true);
    try {
      const r = await adminAxios(token).post('/api/admin/recovery-email', {
        email_recuperacao: novoEmail.trim().toLowerCase(),
        senha_atual: senhaAtual,
      });
      setFeedback({ type: 'ok', msg: r.data?.message || 'E-mail salvo. Confirme pelo link enviado.' });
      setNovoEmail('');
      setSenhaAtual('');
      await reload();
    } catch (err) {
      setFeedback({ type: 'err', msg: err?.response?.data?.detail || 'Não foi possível salvar.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#1E3A5F]" /></div>;

  const hasEmail = me?.tem_email_recuperacao;
  const verified = me?.email_recuperacao_verificado;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-profile-page">
      <div>
        <h1 className="text-2xl font-black text-[#0F1C2E]">Meu perfil</h1>
        <p className="text-xs text-[#475569]">Dados da conta e canais de recuperação</p>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 space-y-3">
        <h3 className="text-sm font-bold text-[#0F1C2E] flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Identidade</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Nome</p>
            <p className="text-sm font-medium text-[#0F1C2E]">{me.nome}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">E-mail de acesso</p>
            <p className="text-sm font-medium text-[#0F1C2E]">{me.email}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Papel</p>
            <p className="text-sm font-medium text-[#0F1C2E]">{me.role === 'super_admin' ? 'Super Admin' : 'Visualizador'}</p>
          </div>
          {me.ultimo_acesso && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Último acesso</p>
              <p className="text-sm font-medium text-[#0F1C2E]">{new Date(me.ultimo_acesso).toLocaleString('pt-BR')}</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 space-y-4" data-testid="admin-recovery-section">
        <h3 className="text-sm font-bold text-[#0F1C2E] flex items-center gap-2"><Mail className="w-4 h-4" /> E-mail de recuperação</h3>

        {hasEmail ? (
          <div
            className={`flex items-start gap-2 p-3 rounded text-xs ${
              verified ? 'bg-[#DCFCE7] border border-[#BBF7D0] text-[#006030]' : 'bg-[#FEF3C7] border border-[#FDE68A] text-[#92400E]'
            }`}
            data-testid="admin-recovery-status"
          >
            {verified ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
            <div>
              <p className="font-bold">
                {me.email_recuperacao_mascarado} {verified ? '· verificado' : '· aguardando confirmação'}
              </p>
              {!verified && <p>Verifique sua caixa de entrada e clique no link enviado. O link expira em 24 horas.</p>}
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 p-3 rounded bg-[#FEF3C7] border border-[#FDE68A] text-[#92400E] text-xs">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Você ainda não cadastrou um e-mail de recuperação. Sem ele, perder o acesso à sua conta exigirá
              intervenção manual de outro Super Admin.
            </span>
          </div>
        )}

        {feedback && (
          <div className={`flex items-start gap-2 p-3 rounded text-xs ${
            feedback.type === 'ok'
              ? 'bg-[#DCFCE7] border border-[#BBF7D0] text-[#006030]'
              : 'bg-[#FEE2E2] border border-[#FECACA] text-[#7F1D1D]'
          }`}
          data-testid="admin-recovery-feedback">
            {feedback.type === 'ok' ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
            <span>{feedback.msg}</span>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-3" data-testid="admin-recovery-form">
          <label className="block">
            <span className="text-[11px] font-medium text-[#475569] uppercase tracking-wider">
              {hasEmail ? 'Novo e-mail de recuperação' : 'Cadastrar e-mail de recuperação'}
            </span>
            <input
              type="email"
              required
              value={novoEmail}
              onChange={(e) => setNovoEmail(e.target.value)}
              placeholder="exemplo: pessoal@gmail.com"
              className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm focus:outline-none focus:border-[#1E3A5F]"
              data-testid="admin-recovery-email-input"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium text-[#475569] uppercase tracking-wider flex items-center gap-1">
              <KeyRound className="w-3 h-3" /> Confirme com sua senha atual
            </span>
            <input
              type="password"
              required
              value={senhaAtual}
              onChange={(e) => setSenhaAtual(e.target.value)}
              autoComplete="current-password"
              className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm focus:outline-none focus:border-[#1E3A5F]"
              data-testid="admin-recovery-pwd-input"
            />
          </label>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-[#1E3A5F] hover:bg-[#16304b] text-white font-bold px-4 py-2 rounded text-sm disabled:opacity-60"
            data-testid="admin-recovery-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {hasEmail ? 'Atualizar e-mail' : 'Cadastrar e-mail'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminProfile;
